import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle } from "lucide-react";
import CompactSegmentCard from "./CompactSegmentCard";
import OrderSummary from "./OrderSummary";
import { mapEtapaToSegment } from "@/lib/utils";
import { formatCurrency } from "@/lib/formatters";
import { isProductBonusEnabledForCycle } from "@/features/productBonusFeature";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
  product_type: number;
}

interface SimulationSummary2027Props {
  orderItems: OrderItem[];
  cycleYear?: number;
}

/**
 * Resumo da simulação de um ciclo futuro — com Resumo por Segmento e Resumo de Valores, sem botão de finalizar pedido.
 */
const SimulationSummary2027 = ({ orderItems, cycleYear = 2027 }: SimulationSummary2027Props) => {
  const isProductBonusEnabled = isProductBonusEnabledForCycle(cycleYear);

  // Group items by segment for summary display
  // Quando a bonificação está ativa, os materiais de Professor ganham um card próprio.
  const itemsBySegment = orderItems.reduce((acc, item) => {
    const segment = isProductBonusEnabled && item.utilizacao === "Professor"
      ? "Professor"
      : mapEtapaToSegment(item.serie);
    if (!acc[segment]) {
      acc[segment] = [];
    }
    acc[segment].push(item);
    return acc;
  }, {} as Record<string, OrderItem[]>);

  if (isProductBonusEnabled && !itemsBySegment.Professor) {
    itemsBySegment.Professor = [];
  }

  // Get segment icon
  const getSegmentIcon = (segment: string) => {
    const icons: Record<string, string> = {
      "Ed. Infantil": "🧸",
      "Fund. Anos Iniciais": "📚",
      "Fund. Anos Finais": "📖",
      "Ensino Médio": "🎓",
      Professor: "👨‍🏫",
      PV: "💼",
      Outros: "📦",
    };
    return icons[segment] || "📦";
  };

  // Sort segments
  const getSegmentOrder = (segmento: string): number => {
    const order: Record<string, number> = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      PV: 5,
      Professor: 6,
      Outros: 999,
    };
    return order[segmento] || 999;
  };

  const sortedSegments = Object.keys(itemsBySegment).sort(
    (a, b) => getSegmentOrder(a) - getSegmentOrder(b)
  );

  // Grid classes
  const getDynamicGridClasses = (segmentCount: number): string => {
    switch (segmentCount) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-1 sm:grid-cols-2";
      case 3:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
      case 4:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
      default:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";
    }
  };

  const gridClasses = getDynamicGridClasses(sortedSegments.length);

  // Subtotais e Totais
  const getGrossSubtotal = () => {
    return orderItems
      .filter((item) => item.delivery_type_number === 1 && item.quantidade_pedido > 0)
      .reduce((total, item) => total + item.preco * item.quantidade_pedido, 0);
  };

  const getSubtotal = () => {
    return orderItems
      .filter((item) => item.delivery_type_number === 2 && item.quantidade_pedido > 0)
      .reduce((total, item) => total + item.valor_liquido * item.quantidade_pedido, 0);
  };

  const getTotal = () => {
    return orderItems
      .filter((item) => item.quantidade_pedido > 0)
      .reduce((total, item) => total + item.valor_total, 0);
  };

  return (
    <div className="space-y-6">
      {/* Resumo por Segmento */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            📊 Resumo por Segmento — Simulação {cycleYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid gap-4 ${gridClasses}`}>
            {sortedSegments.map((segmento) => {
              const items = itemsBySegment[segmento];
              const icon = getSegmentIcon(segmento);

              return (
                <CompactSegmentCard
                  key={segmento}
                  segmentName={segmento}
                  items={items}
                  formatCurrency={formatCurrency}
                  icon={icon}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Resumo de Valores */}
      <OrderSummary
        cycleYear={cycleYear}
        grossSubtotal={getGrossSubtotal()}
        netSubtotal={getSubtotal()}
        total={getTotal()}
        freteAnual={0}
        freteSemestral={0}
        orderItems={orderItems}
        formatCurrency={formatCurrency}
        isScheduled={false}
        hideFinalizeButton={true}
        hideInstallmentPlan={true}
      />

      {/* Aviso: sem ação de finalizar */}
      <Card className="border-amber-300 bg-amber-50/50">
        <CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>Simulação do Ciclo {cycleYear}.</strong> Não serão gerados pedidos
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SimulationSummary2027;
